"use client";

import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from "react";

/**
 * Matches PageBuilder `scroll-mt-32` (UtilityBar + NavBar) so anchored
 * sections are not clipped under the sticky header.
 */
const STICKY_HEADER_OFFSET_PX = 128;

type SmoothScrollAnchorProps = {
  children: ReactNode;
  className?: string;
  href: `#${string}`;
  /**
   * Extra pixels past the target top after the sticky-header offset.
   * Positive values scroll further down (hides more of the content above).
   */
  scrollPastOffset?: number;
  /**
   * When set, `scrollPastOffset` only applies at/above this viewport width
   * (CSS px). Below that, past offset is 0 so the sticky header still clears
   * the section title.
   */
  scrollPastOffsetFromWidth?: number;
} & Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  "children" | "className" | "href" | "onClick"
>;

function resolveScrollPastOffset(
  scrollPastOffset: number,
  scrollPastOffsetFromWidth: number | undefined,
): number {
  if (
    scrollPastOffsetFromWidth !== undefined &&
    !window.matchMedia(`(min-width: ${scrollPastOffsetFromWidth}px)`).matches
  ) {
    return 0;
  }

  return scrollPastOffset;
}

function SmoothScrollAnchor({
  children,
  className,
  href,
  scrollPastOffset = 0,
  scrollPastOffsetFromWidth,
  ...props
}: SmoothScrollAnchorProps) {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    const sectionId = href.slice(1);
    const section = document.getElementById(sectionId);
    if (!section) {
      return;
    }

    event.preventDefault();

    const sectionTop = section.getBoundingClientRect().top + window.scrollY;
    const pastOffset = resolveScrollPastOffset(
      scrollPastOffset,
      scrollPastOffsetFromWidth,
    );

    window.scrollTo({
      behavior: "smooth",
      top: sectionTop - STICKY_HEADER_OFFSET_PX + pastOffset,
    });
    window.history.replaceState(null, "", href);
  };

  return (
    <a className={className} href={href} onClick={handleClick} {...props}>
      {children}
    </a>
  );
}

export { SmoothScrollAnchor };
